import BigNumber from "bignumber.js";
import { UNIT } from "../../prototype/big-number";
import { BlackScholesLookup } from "./black-scholes/black-scholes-lookup";
import { BlackScholes } from "./black-scholes/black-scholes";
import { environment } from "src/environments/environment";

export interface GetPremiumParams {
  now: number;
  spot: BigNumber;
  riskFreeRate: BigNumber;
  initialMarginRiskRate: BigNumber;
  spotFee: BigNumber;
  optionFee: BigNumber;
  minPremium: BigNumber;
  expiry: number;
  strike: BigNumber;
  iv: BigNumber;
  size: BigNumber;
  available: BigNumber;
  equity: BigNumber;
  priceRatio: BigNumber;
  priceRatio2: BigNumber;
  priceRatioUtilization: BigNumber;
  isCall: boolean;
}

export const INT256_MAX = new BigNumber('57896044618658097711785492504343953926634992332820282019728792003956564819967');

export class OptionPricer {
  blackScholesLookup: BlackScholesLookup = new BlackScholesLookup();
  blackScholes: BlackScholes = new BlackScholes();

  getPremium(params: GetPremiumParams) {
    if (params.iv.lte(0)) {
      throw new Error('iv is 0');
    }
    const isBuy = params.size.gt(0);
    const absSize = isBuy ? params.size : params.size.times(-1);
    const bs = environment.features.blackScholesLookup ? this.blackScholesLookup : this.blackScholes;
    let price = bs.getPrice(params.isCall, params.expiry, params.expiry - params.now, params.iv, params.spot, params.strike, params.riskFreeRate);
    if (!params.available.eq(INT256_MAX)) {
      price = this.adjustPriceByUtilization(params, price, isBuy);
    }
    if (isBuy && price.lt(params.minPremium)) {
      price = params.minPremium;
    }
    let fee = params.spot.decimalMul(params.spotFee).plus(price.decimalMul(params.optionFee));
    if (!isBuy && fee.gt(price)) {
      fee = price;
    }
    return [
      isBuy ? price.decimalMul(absSize).times(-1) : price.decimalMul(absSize),
      fee.decimalMul(absSize).times(-1)
    ];
  }

  private adjustPriceByUtilization(params: GetPremiumParams, price: BigNumber, isBuy: boolean) {
    if (params.available.gt(params.equity)) {
      params.available = params.equity;
    }
    if (params.available.lte(0)) {
      // throw new Error('available must be greater than 0');
      return isBuy ? price.decimalMul(UNIT.plus(UNIT)) : price.decimalDiv(UNIT.plus(UNIT));
    }
    let utilization = UNIT.minus(params.available.decimalDiv(params.equity));
    let utilizationAfter;
    let availableAfter: BigNumber;
    // pool sell
    if (isBuy) {
      const maxRisk = params.size.decimalMul(params.spot);
      const initialMarginChange = maxRisk.decimalMul(params.initialMarginRiskRate);
      availableAfter = params.available.minus(initialMarginChange);
    } else {
      const value = price.decimalMul(params.size);
      availableAfter = params.available.plus(value);
    }
    utilizationAfter = availableAfter.gte(0) ? UNIT.minus(availableAfter.decimalDiv(params.equity)) : UNIT;
    let utilizationAdjust;
    if (utilization.lt(params.priceRatioUtilization) && utilizationAfter.gt(params.priceRatioUtilization)) {
      const area1 = this.getArea(new BigNumber(0), new BigNumber(0), params.priceRatioUtilization, params.priceRatio, utilization.plus(params.priceRatioUtilization).div(2), params.priceRatioUtilization.minus(utilization));
      const area2 = this.getArea(params.priceRatioUtilization, params.priceRatio, UNIT, params.priceRatio2, params.priceRatioUtilization.plus(utilizationAfter).div(2), utilizationAfter.minus(params.priceRatioUtilization));
      utilizationAdjust = area1.plus(area2).decimalDiv(utilizationAfter.minus(utilization));
    } else {
      utilization = utilization.plus(utilizationAfter).divEvm(2);
      if (utilization.gte(params.priceRatioUtilization)) {
        utilizationAdjust = this.getY(params.priceRatioUtilization, params.priceRatio, UNIT, params.priceRatio2, utilization);
      } else {
        utilizationAdjust = this.getY(new BigNumber(0), new BigNumber(0), params.priceRatioUtilization, params.priceRatio, utilization);
      }
    }
    utilizationAdjust = utilizationAdjust.plus(UNIT);
    return isBuy ? price.decimalMul(utilizationAdjust) : price.decimalDiv(utilizationAdjust);
  }

  // x: utilization
  // y: adjust
  private getY(x1: BigNumber, y1: BigNumber, x2: BigNumber, y2: BigNumber, x: BigNumber) {
    return y2.minus(y1).decimalDiv(x2.minus(x1)).decimalMul(x.minus(x1)).plus(y1);
  }

  private getArea(x1: BigNumber, y1: BigNumber, x2: BigNumber, y2: BigNumber, x: BigNumber, w: BigNumber) {
    return this.getY(x1, y1, x2, y2, x).decimalMul(w);
  }
}
